import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import jsonData from "./pageconfig.json";
import ParagraphLayout from "../layouts/ParagraphLayout";
import CardLayout from "../layouts/CardLayout";
import BoxLayout from "../layouts/BoxLayout";
import ListLayout from "../layouts/ListLayout";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import FeatIcon1 from "../../../src/assets/images/feat-icon-1.png";
import FeatIcon2 from "../../../src/assets/images/feat-icon-2.png";
import FeatIcon3 from "../../../src/assets/images/feat-icon-3.png";
import FeatIcon4 from "../../../src/assets/images/feat-icon-4.png";
import Instructor from "../../../src/assets/images/cm_instructor.png";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import { Fab, Zoom, useScrollTrigger } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Footer from "./Footer";
import {
  Box,
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
  Paper,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";

const features = [
  {
    icon: FeatIcon4,
    alt: "Sounds, sounds, sounds",
    title: "Sounds, sounds, sounds",
    description:
      "Learn foundational Arabic sounds to unlock native-like pronunciation.",
    color: "linear-gradient(to right, #6a11cb, #2575fc)",
  },
  {
    icon: FeatIcon3,
    alt: "20/80 Quranic Arabic",
    title: "20/80 Quranic Arabic",
    description: "Focus on patterns that appear 80% of the time in the Quran.",
    color: "linear-gradient(to right, #f7971e, #ffd200)",
  },
  {
    icon: FeatIcon1,
    alt: "Skip Memorization",
    title: "Skip Memorization",
    description: "Understand through sound and meaning, not rote learning.",
    color: "linear-gradient(to right, #00b09b, #96c93d)",
  },
  {
    icon: FeatIcon2,
    alt: "Quranic Arabic Drills",
    title: "Quranic Arabic Drills",
    description: "Reinforce learning with structured, native-like practice.",
    color: "linear-gradient(to right, #fc6076, #ff9a44)",
    link: "class-details.html",
  },
];

const Home = ({
  aboutRef,
  instRef,
  testimonialRef,
  compareProRef,
  QARef,
  contactRef,
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const aboutPoints = [
    "1-year online program",
    "2 hours a week",
    "Enrollment begins in April",
    "Classes start first week of May",
    "$1500 Tuition fee for the entire course",
    "All course materials will be online",
    "Lifetime access to your course.",
    "Seats are limited and allocated on a first-come, first-served basis",
  ];

  const prerequisites = [
    "Ability to read. Fluency is optional",
    "Anyone aged 13 and older, regardless of gender",
    "Familiarity with using websites, internet, and Zoom",
    "Basic understanding of English, as it will be our main language of communication",
    "Full commitment and undivided attention",
  ];

  return (
    <Grid container sx={{ width: "100%", mt: 2 }}>
      {/* Top Section */}
      <Box sx={{ width: "100%", backgroundColor: "#06456c", m: 2 }}>
        <Typography
          variant="h3"
          align="center"
          sx={{ fontFamily: "Rubik", fontWeight: 600, mb: 3, color: "#ffffff" }}
        >
          Last Day for Enrollment May 15th 2025
        </Typography>
      </Box>
      {/* First Feature Section */}
      <Box sx={{ py: 4, width: "100%" }}>
        <Container>
          <Typography
            variant="h4"
            align="left"
            sx={{ fontWeight: "bold", mb: 3, fontFamily: "Rubik" }}
          >
            Transform Your Quranic Arabic Journey
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              {/* Placeholder for left column */}
              Have you tried short-term and weekend programs that promise much
              but leave you with little to show for it? Have you learned with
              friends who aren’t trained teachers, or taken hourly courses that
              ended up teaching you nothing truly useful? Perhaps you’ve even
              scoured countless online resources, only to be overwhelmed by the
              sheer volume of conflicting information.
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* Placeholder for center column */}
              If any of these scenarios sound familiar, you’re not alone—and
              there’s a better way. Our comprehensive course is designed as a
              transformative journey into the heart of the Quran. We’ve
              developed an <strong>objective-driven curriculum</strong> refined
              over years of experience that goes far beyond fragmented lessons
              and outdated methods.
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: "1.1rem" }}>
                Imagine learning Arabic through <strong>natural sounds</strong>,{" "}
                <strong>intuitive patterns</strong>, and{" "}
                <strong>targeted drills</strong> that help you truly internalize
                the language. Each lesson is crafted to build lasting mastery,
                opening the door to the <strong>divine wisdom</strong> of the
                Quran in a way that’s both engaging and unforgettable.
              </Typography>
            </Grid>
          </Grid>
          {/* Buttons */}
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography sx={{ fontSize: "large", p: 2 }}>
              Acquire Arabic like a native through sounds and patterns—with no
              memorization.
            </Typography>
            <Link to="/enroll">
              <Button
                variant="contained"
                color="primary"
                sx={{
                  mx: 1,
                  borderRadius: 0,
                  width: "100px",
                  fontWeight: "bold",
                }}
                href="enroll.html"
              >
                Enroll
              </Button>
            </Link>
            <Button
              variant="contained"
              color="primary"
              sx={{
                borderRadius: 0,
                width: "auto",
                fontWeight: "bold",
              }}
              href="#aboutcourse"
            >
              Learn more
            </Button>
          </Box>
          <Box ref={aboutRef} sx={{ mb: 2, mt: 5 }}>
            <Grid container spacing={4} alignItems="center">
              {/* Left: Features */}
              <Grid item xs={12} md={8}>
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" sx={{ mb: 2, fontWeight: "bold" }}>
                    Welcome to Tanween
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    Bringing language to life. Learn Modern Standard Arabic to
                    better understand the Quran.
                  </Typography>
                </Box>

                <Grid container spacing={6}>
                  {features.map((feature, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box display="flex">
                        <Box
                          component="img"
                          src={feature.icon}
                          alt={feature.alt}
                          sx={{ width: 48, height: 48, mr: 2 }}
                        />
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {feature.link ? (
                              <a
                                href={feature.link}
                                style={{
                                  textDecoration: "none",
                                  color: "#000",
                                }}
                              >
                                {feature.title}
                              </a>
                            ) : (
                              feature.title
                            )}
                          </Typography>
                          <Divider
                            sx={{
                              my: 1,
                              height: 4,
                              width: 40,
                              borderRadius: 5,
                              background: feature.color,
                            }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* Right: Video Section */}
              <Grid item xs={12} md={4}>
                <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Program Introduction
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, textAlign: "left" }}
                  >
                    Discover how our unique approach to Quranic Arabic can
                    transform your understanding of the Quran. Watch our short
                    introduction video to learn more about the course and its
                    benefits.
                  </Typography>
                  <Button
                    href="https://www.youtube.com/watch?v=0Fwu7afh9pM"
                    target="_blank"
                    sx={{
                      backgroundColor: "#095e92",
                      color: "#f4efef",
                      px: 3,
                      py: 1.5,
                      borderRadius: "8px",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#06456c",
                      },
                    }}
                    startIcon={<PlayCircleOutlineIcon sx={{ fontSize: 32 }} />}
                  >
                    Watch on YouTube
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ mt: 5 }}>
            <Grid
              container
              spacing={4}
              justifyContent="space-between"
              alignItems="flex-start"
            >
              {/* About the Program */}
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  About the Program
                </Typography>
                <List dense>
                  {aboutPoints.map((text, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
                <Box sx={{ pt: 3 }}>
                  <Button
                    variant="contained"
                    href="enroll.html"
                    sx={{
                      mx: 1,
                      borderRadius: 0,
                      width: "100px",
                      fontWeight: "bold",
                    }}
                  >
                    Enroll
                  </Button>
                </Box>
              </Grid>

              {/* Prerequisites */}
              <Grid item xs={12} md={6}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
                  Prerequisites
                </Typography>
                <List dense>
                  {prerequisites.map((text, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </Box>
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 4 }}
          >
            {/* Text Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: { xs: 4, md: 0 } }}>
                <Box ref={instRef} sx={{ mb: 4 }}>
                  <Typography variant="h3" sx={{ color: "primary.main" }}>
                    {/* Add your headline text here */}
                    Meet The Instructor
                  </Typography>
                  <Typography variant="h4">
                    {/* Add your headline text here */}
                    <span style={{ color: "grey", fontWeight: "800" }}>
                      Chand Mohammed
                    </span>
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    I grew up in India, primarily in a secular environment.
                    Alhamdulillah, once I recognized Islam as the true faith, I
                    aimed to deepen my understanding and practice. To grasp the
                    Quran, I realized I needed to learn Arabic. I spent four to
                    five years trying different resources like books, CDs, and
                    community courses, but they didn't prove effective. This was
                    before the era of Google and YouTube.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    In 2003, my prayers were answered when I began private
                    Arabic lessons with teachers from Egypt. For the next five
                    to six years, I devoted all my time and effort to learning
                    the language, including conversation and tajweed.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    Eventually, a few friends requested that I teach them what I
                    had learned, which led me to begin teaching in 2010. On a
                    side note, I speak about seven languages.
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Image Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ pl: { md: 4 }, width: "100%" }}>
                <img
                  src={Instructor}
                  alt="Instructor Image"
                  style={{ width: "100%", height: "auto" }}
                />
              </Box>
            </Grid>
          </Grid>
          <Box ref={compareProRef}>
            <Typography variant="h4" sx={{ color: "primary.main" }}>
              Compare Programs
            </Typography>
            <Card elevation={3}>
              <CardContent>
                <div style={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <strong></strong>
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          align="center"
                        >
                          TANWEENARABIC
                        </TableCell>
                        <TableCell align="center">QALAM</TableCell>
                        <TableCell align="center">ZAYTUNA</TableCell>
                        <TableCell align="center">GUIDANCE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Objective Driven Curriculim</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Sentence and Expressions</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Basic Conversation</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Intermediate Conversation</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Advanced Conversation</TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Basic الصرف - Morphology</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Advanced الصرف - Morphology</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Basic النحو (الإعراب)</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Advanced النحو (الإعراب)</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Reading Unvowelled Selected Text</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Analyse Quranic Text</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Analyse Hadith Text</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow></TableRow>
                      <TableRow>
                        <TableCell>Rhetorics - البلاغة</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>1400 Quranic Words</TableCell>
                        <TableCell align="center">
                          <CheckIcon color="success" />
                        </TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                        <TableCell align="center">—</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Course Fee</TableCell>
                        <TableCell align="center">$1500</TableCell>
                        <TableCell align="center">~$8000</TableCell>
                        <TableCell align="center">~$20000</TableCell>
                        <TableCell align="center">~$125/credit</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Duration</TableCell>
                        <TableCell align="center">1-Year</TableCell>
                        <TableCell align="center">1-Year</TableCell>
                        <TableCell align="center">3 Semesters</TableCell>
                        <TableCell align="center">9 Credits</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Online/In-person</TableCell>
                        <TableCell align="center">Online</TableCell>
                        <TableCell align="center">In-Person</TableCell>
                        <TableCell align="center">In-Person</TableCell>
                        <TableCell align="center">Hybrid</TableCell>
                      </TableRow>

                      {/* Add more rows as needed */}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </Box>
          <Box ref={testimonialRef} sx={{ mt: 4 }}>
            <Typography
              variant="h4"
              sx={{
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  bottom: -4, // space between text and line
                  width: "80px", // custom underline width
                  height: "2px",
                  backgroundColor: "#00b09b",
                },
              }}
            >
              Testimonials
            </Typography>

            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “After studying with Ustadh Abu Yusuf and doing the program,
                Alhamdulillah I feel strong enough in my understanding of Arabic
                to be able to read and understand the Qur'an as well as most
                Arabic texts. I feel confident applying to the 2nd year of many
                institutions. To be able to study Arabic with a full school or
                work schedule and still reach the level of dedicated scholars is
                a true blessing. I highly recommend the course.”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Syed Mansoor
              </Typography>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “My name is Adib Baji and I am one of the 'alumni' of Br.Chand's
                Arabic Through Stories class. We started in 2014 and over the
                course of 7 years we went over several stories, surahs, and
                hadiths to help us break down the meanings of individual
                sentences, phrases, words, and sometimes even individual
                letters. With a heavy focus on grammatical structure, we were
                taught to be able to analyze sentence composition and word
                patterns on command. Due to this, despite my personally weak
                vocabulary, I was still able to grasp the meaning of the
                majority of sentences. Inshallah may future students have even
                greater success under Br.Chand's guidance (especially after
                using us as guinea pigs 😆)”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Adib Baji
              </Typography>
            </Paper>

            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “Alhamdulillah I started learning Arabic with Ustadh Chand in
                2014. I appreciated the dedication he had to the class. I saw
                him make some homemade Arabic work books for us. I also
                appreciated how after COVID, we still continued the classes
                online. There was always a good amount of homework that
                reinforced what we had learned in the class. Once we got into
                the more advanced classes, we read Surah Baqarah together and I
                was amazed at how much I understood without an English
                translation. This Arabic class is convenient for all: high
                school or college students, young professionals, and
                stay-at-home or working parents. Ustadh Chand himself is very
                accommodating, knowledgeable, and passionate about the subject
                matter.”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Nimrah
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “Alhamdulillah, I am delighted to provide this testimonial for
                Ustad Chand, who has been an exceptional Arabic language
                instructor. Under his guidance, I have not only gained a
                comprehensive understanding of Arabic grammar but have also
                developed a profound appreciation for the language's intricacies
                and nuances. Masha Allah, his methodology to teach with the
                sounds of Arabic Sarf, the grammar and the encouragement and
                motivation are exceptional. Ustad Chand possesses a rare
                combination of expertise, patience, and passion for teaching,
                which has made every lesson engaging and enlightening. His
                dedication to ensuring that every student grasps the material is
                truly commendable. Whether it's elucidating complex grammar
                rules or refining pronunciation, Bro Chand employs innovative
                teaching methods that cater to individual learning styles,
                making the learning process both effective and enjoyable.
                Moreover, Masha Allah, Ustad Chand goes above and beyond by
                providing additional resources, offering constructive feedback,
                and fostering a supportive learning environment. His unwavering
                commitment to his students' success is evident in the progress
                I've made under his tutelage. Alhamdulillah, by the help of
                Allah and through Bro Chand’s teachings, I am able to comprehend
                the Qur'an—the ultimate purpose of learning the language. I now
                feel confident in my ability to communicate effectively in
                Arabic, both verbally and in writing. I wholeheartedly recommend
                Ustad Chand as an Arabic language instructor.”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Badr Zaman
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “We attended the Arabic course conducted by Br.Chand together as
                a family. The course provided us an opportunity to get closer to
                the nuances of the Arabic language and the preciseness and
                elegance of Arabic grammar. Our understanding of the Qur'an
                after this course has helped us establish an ongoing
                relationship with the Quran. The course allowed us to have live
                interactions with Br.Chand, whose deep knowledge of the language
                was invaluable in getting grounded in its richness. The
                experience has been a cornerstone in establishing our grasp of
                the language. Thank you for being so patient and encouraging
                throughout our learning journey.”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Nazima
              </Typography>
            </Paper>
            <Paper elevation={2} sx={{ p: 3, my: 2, borderRadius: 2 }}>
              <Typography variant="body1" mb={2}>
                “I was really amazed that Ustadh Abu Yusuf was able to combine
                both speaking and understanding in a single course, compared to
                other courses that only focus on one or the other.”
              </Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                – Asma
              </Typography>
            </Paper>
          </Box>

          <Box ref={QARef} sx={{ py: 5 }}>
            <Typography
              variant="h4"
              gutterBottom
              sx={{ color: "primary.main" }}
            >
              Frequently Asked Questions
            </Typography>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Program Admissions
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Last day to enroll:</strong> May 15th
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>After enrollment: </strong> A short interview
                        will be held to understand your commitment and set
                        expectations
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Post-interview:</strong> Tuition payment details
                        will be sent to you
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Payment deadline:</strong> June 15th
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Seats are limited:</strong> First-come,
                        first-served basis with small class sizes — please
                        enroll soon
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>After payment:</strong> Course registration
                        details will be shared
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Next steps:</strong> Complete all course and
                        roster registration setup
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>First class:</strong> Scheduled for July 12th,
                        inshaa Allah
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Course Fees & Payment Info
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Total tuition: </strong> $1500
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Preferred: </strong> Full payment before the
                        first class
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Installment option: </strong> Pay 60% before
                        class starts, remaining balance by September 30th
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Family/Group Discount: </strong> 15% off when
                        enrolling together
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>No application fee </strong>
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Note: </strong> Tuition is non-refundable
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Course Duration & Schedule
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Duration: </strong> 1-year course
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Class time: </strong> Sundays, 9:00–11:00 AM CST
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>End date: </strong> August 15th, 2026
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Class format: </strong> Online via Zoom
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>No classes during: </strong> Ramadan
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Breaks: </strong> Thanksgiving weekend and Eid
                        (if it falls on a Saturday)
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Prerequisites & Content
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Must be able to read: </strong> fluency is not
                        required
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Arabic knowledge: </strong> No prior knowledge
                        needed
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Course focus: </strong> Arabic grammar, with the
                        goal of deepening understanding and connection with the
                        Quran
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Reading: </strong> Limited in-class reading to
                        optimize time; additional reading activities available
                        through mobile and AI-powered app
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Assignments: </strong> Weekly assignments
                        starting at ~2 hours per week, increasing to ~4 hours
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Assessments: </strong> Online quizzes to track
                        progress
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Mobile app: </strong> Includes vocabulary
                        practice, revision drills, and reading activities
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Class leaderboard: </strong> Track progress and
                        stay motivated with a friendly leaderboard for reading
                        and assignments
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Class recordings: </strong> TAll classes will be
                        recorded and shared at the end of each month
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Materials: </strong> All content is provided
                        online through our learning platform — no books or
                        external materials required
                      </Typography>
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 16 }}>
                    <CircleIcon sx={{ fontSize: 8, color: "text.secondary" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography component="span">
                        <strong>Course design: </strong> Not based on a specific
                        book; the curriculum is built from over a decade of
                        teaching experience to serve its unique objectives
                      </Typography>
                    }
                  />
                </ListItem>
              </List>
            </Box>
          </Box>
          <Box ref={contactRef}>
            <Typography
              variant="h4"
              gutterBottom
              fontWeight="bold"
              color="primary.main"
            >
              Have a Question?
            </Typography>
            <Box sx={{ mt: 3 }}>
              <iframe
                src="https://docs.google.com/forms/d/e/1FAIpQLSeC5jbjULYMqRlHRI4uzpn0TSSMFTjSHkFgkSlWfpiRQxPwtw/viewform?embedded=true"
                width="100%"
                height="600"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Google Form"
              >
                Loading…
              </iframe>
            </Box>
          </Box>
        </Container>
      </Box>
      <Footer />
    </Grid>
  );
};

export default Home;
